/**
 * Campus Valley 360° — Page Leçon.
 * Lecteur multimédia (vidéo/audio/PDF/Excel) + contenu texte + quiz + chat IA.
 */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    ArrowLeft, ArrowRight, CheckCircle2, Clock, Download, FileText,
    Headphones, Video, Send, Sparkles, Loader2, BookOpen, ListTree, Share2,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { renderRichContent } from '@/lib/sanitizeHtml';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Convert a YouTube or Vimeo URL into its embeddable form.
 * Returns null if the URL is a direct media file (mp4, webm, m3u8, …).
 * Supports : youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/embed/<id>,
 *            vimeo.com/<id>, player.vimeo.com/video/<id>
 */
function toEmbedUrl(url) {
    if (!url || typeof url !== 'string') return null;
    const u = url.trim();
    // YouTube short
    let m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
    if (m) {
        const id = m[1];
        return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`;
    }
    // Vimeo
    m = u.match(/(?:vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?|player\.vimeo\.com\/video\/)(\d{4,})/);
    if (m) {
        const id = m[1];
        return `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&dnt=1`;
    }
    return null;
}

export default function CampusLesson() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { session, user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const { lang } = useLang();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({}); // { qIdx: optionIdx }
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [quizScore, setQuizScore] = useState(null);
    const [savingProgress, setSavingProgress] = useState(false);
    // AI chat state
    const [aiHistory, setAiHistory] = useState([]);
    const [aiInput, setAiInput] = useState('');
    const [aiBusy, setAiBusy] = useState(false);
    const aiScrollRef = useRef(null);
    const startTimeRef = useRef(Date.now());

    const reload = async () => {
        if (!session) return;
        try {
            const r = await axios.get(`${API}/campus/lessons/${id}`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setData(r.data);
            // mark started
            axios.post(`${API}/campus/lessons/${id}/start`, {}, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            }).catch(() => {});
            // load AI history if enabled
            if (r.data.lesson.ai_enabled) {
                axios.get(`${API}/campus/lessons/${id}/ai-history`, {
                    headers: { Authorization: `Bearer ${session.access_token}` },
                }).then(rr => setAiHistory(rr.data.items || [])).catch(() => {});
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        startTimeRef.current = Date.now();
        setQuizSubmitted(false);
        setAnswers({});
        setQuizScore(null);
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, session]);

    useEffect(() => {
        if (aiScrollRef.current) aiScrollRef.current.scrollTop = aiScrollRef.current.scrollHeight;
    }, [aiHistory, aiBusy]);

    // Reading progress (page scroll) + position memory
    const [readProgress, setReadProgress] = useState(0);
    const [resumePos, setResumePos] = useState(null);
    const titleRef = useRef('');
    useEffect(() => {
        titleRef.current = data?.lesson ? ((lang === 'fr' ? data.lesson.title_fr : data.lesson.title_en) || data.lesson.title_fr || '') : '';
    }, [data, lang]);
    useEffect(() => {
        try {
            const raw = localStorage.getItem(`av-lesson-pos-${id}`);
            let saved = 0;
            if (raw) {
                try { const v = JSON.parse(raw); saved = typeof v === 'number' ? v : (v?.y || 0); }
                catch { saved = parseInt(raw, 10) || 0; }
            }
            setResumePos(saved > 500 ? saved : null);
        } catch { setResumePos(null); }
        let saveTimer = null;
        const onScroll = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const pct = max > 0 ? Math.min(100, Math.max(0, (window.scrollY / max) * 100)) : 0;
            setReadProgress(pct);
            clearTimeout(saveTimer);
            saveTimer = setTimeout(() => {
                try {
                    if (window.scrollY > 200 && window.scrollY < max - 100) {
                        localStorage.setItem(`av-lesson-pos-${id}`, JSON.stringify({
                            y: Math.round(window.scrollY), pct: Math.round(pct), t: titleRef.current,
                        }));
                    } else if (window.scrollY >= max - 100) {
                        localStorage.removeItem(`av-lesson-pos-${id}`);
                    }
                } catch { /* ignore */ }
            }, 400);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => { clearTimeout(saveTimer); window.removeEventListener('scroll', onScroll); };
    }, [id]);

    const resumeReading = () => {
        window.scrollTo({ top: resumePos, behavior: 'smooth' });
        setResumePos(null);
    };

    // Share lesson with a workspace colleague
    const [shareOpen, setShareOpen] = useState(false);
    const [members, setMembers] = useState(null);
    const [sharing, setSharing] = useState(null);
    const openShare = async () => {
        setShareOpen(true);
        if (members !== null || !currentWorkspace) return;
        const { data: rows } = await supabase.from('workspace_members')
            .select('user_id, profiles(full_name, email)')
            .eq('workspace_id', currentWorkspace.id);
        setMembers((rows || []).filter(m => m.user_id !== user?.id));
    };
    const recommend = async (m) => {
        setSharing(m.user_id);
        try {
            await axios.post(`${API}/campus/lessons/${id}/recommend`,
                { to_user_id: m.user_id, workspace_id: currentWorkspace?.id, lang },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            const name = m.profiles?.full_name || m.profiles?.email || '';
            toast.success(lang === 'fr' ? `Leçon recommandée à ${name}` : `Lesson recommended to ${name}`);
            setShareOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setSharing(null);
    };

    // Body HTML + table of contents (ids injected into h2/h3)
    const rawBody = data?.lesson ? (lang === 'fr' ? data.lesson.body_fr : data.lesson.body_en) || '' : '';
    const { bodyHtml, toc } = useMemo(() => {
        const html = renderRichContent(rawBody);
        if (!html) return { bodyHtml: '', toc: [] };
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const entries = [];
            doc.querySelectorAll('h2, h3').forEach((h, i) => {
                const anchor = `section-${i}`;
                h.id = anchor;
                entries.push({ id: anchor, text: h.textContent, level: h.tagName === 'H2' ? 2 : 3 });
            });
            return { bodyHtml: doc.body.innerHTML, toc: entries };
        } catch {
            return { bodyHtml: html, toc: [] };
        }
    }, [rawBody]);

    const scrollToSection = (anchor) => {
        document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;
    if (!data?.lesson) {
        return (
            <div className="text-center py-20">
                <div className="text-2xl font-heading mb-3">{lang === 'fr' ? 'Leçon introuvable' : 'Lesson not found'}</div>
                <Link to="/app/formation" className="text-primary hover:underline">← {lang === 'fr' ? 'Retour au Campus' : 'Back to Campus'}</Link>
            </div>
        );
    }

    const lesson = data.lesson;
    const siblings = data.siblings || [];
    const currentIdx = siblings.findIndex(s => s.id === lesson.id);
    const prevLesson = currentIdx > 0 ? siblings[currentIdx - 1] : null;
    const nextLesson = currentIdx >= 0 && currentIdx < siblings.length - 1 ? siblings[currentIdx + 1] : null;

    const quiz = Array.isArray(lesson.quiz) ? lesson.quiz : [];
    const completed = lesson.user_progress?.status === 'completed';
    const passScore = lesson.pass_score || 70;

    const submitQuiz = async () => {
        if (Object.keys(answers).length !== quiz.length) {
            toast.error(lang === 'fr' ? 'Répondez à toutes les questions' : 'Answer all questions');
            return;
        }
        let correct = 0;
        quiz.forEach((q, i) => { if (answers[i] === q.correct) correct += 1; });
        const score = Math.round((correct / quiz.length) * 100);
        setQuizScore(score);
        setQuizSubmitted(true);
        if (score < passScore) {
            toast.warning(lang === 'fr' ? `Score : ${score}% — Seuil ${passScore}% non atteint` : `Score: ${score}% — Below ${passScore}%`);
            return;
        }
        // Save progress
        setSavingProgress(true);
        try {
            const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
            await axios.post(`${API}/campus/lessons/${id}/complete`,
                { quiz_score: score, quiz_answers: answers, time_spent_sec: elapsed },
                { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            toast.success(lang === 'fr' ? `Leçon validée — Score ${score}%` : `Lesson completed — Score ${score}%`);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setSavingProgress(false);
        }
    };

    const markCompleteNoQuiz = async () => {
        setSavingProgress(true);
        try {
            const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
            await axios.post(`${API}/campus/lessons/${id}/complete`,
                { time_spent_sec: elapsed },
                { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            toast.success(lang === 'fr' ? 'Leçon validée' : 'Lesson completed');
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setSavingProgress(false);
        }
    };

    const askAi = async () => {
        const msg = aiInput.trim();
        if (!msg) return;
        setAiInput('');
        setAiBusy(true);
        // optimistic
        setAiHistory(h => [...h, { role: 'user', content: msg, id: `tmp-${Date.now()}` }]);
        try {
            const r = await axios.post(`${API}/campus/lessons/${id}/ai-ask`,
                { message: msg, lang },
                { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            setAiHistory(h => [...h, { role: 'assistant', content: r.data.reply, id: `tmp2-${Date.now()}` }]);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
            setAiHistory(h => h.slice(0, -1));
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <div className="space-y-8" data-testid={`lesson-page-${lesson.slug}`}>
            {/* Reading progress bar */}
            <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-transparent pointer-events-none" data-testid="lesson-read-progress">
                <div className="h-full bg-primary transition-[width] duration-150 ease-out"
                     style={{ width: `${readProgress}%` }} />
            </div>
            {/* Resume reading pill */}
            {resumePos !== null && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-ink text-white shadow-lg pl-4 pr-2 py-2" data-testid="lesson-resume-pill">
                    <span className="text-sm">{lang === 'fr' ? 'Reprendre où vous vous étiez arrêté·e ?' : 'Resume where you left off?'}</span>
                    <button type="button" onClick={resumeReading}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium px-3.5 py-1.5 transition-colors"
                        data-testid="lesson-resume-btn">{lang === 'fr' ? 'Reprendre' : 'Resume'}</button>
                    <button type="button" onClick={() => setResumePos(null)} aria-label="Fermer"
                        className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70"
                        data-testid="lesson-resume-dismiss">✕</button>
                </div>
            )}
            <Link to="/app/formation" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft size={14} className="mr-1.5" /> {lang === 'fr' ? 'Campus' : 'Campus'}
            </Link>

            <div className="grid lg:grid-cols-3 gap-8">
                {/* MAIN */}
                <div className="lg:col-span-2 space-y-6">
                    <div>
                        <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground mb-3">
                            <BookOpen size={12} />
                            <span>{lesson.parent_type === 'pillar' ? (lang === 'fr' ? 'Pilier' : 'Pillar') : (lang === 'fr' ? 'Cas d\'usage' : 'Use case')}</span>
                            {lesson.duration_min && <><span>·</span><span>{lesson.duration_min} min</span></>}
                            {completed && <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 size={12} /> {lang === 'fr' ? 'Validée' : 'Completed'} ({lesson.user_progress?.quiz_score}%)</span>}
                        </div>
                        <h1 className="font-heading text-4xl leading-tight mb-2">{lang === 'fr' ? lesson.title_fr : lesson.title_en}</h1>
                        {lesson.summary_fr && (
                            <p className="text-foreground/70 leading-relaxed">{lang === 'fr' ? lesson.summary_fr : lesson.summary_en}</p>
                        )}
                        <Button onClick={openShare} variant="outline" size="sm" className="mt-4" data-testid="lesson-share-btn">
                            <Share2 size={14} className="mr-1.5" /> {lang === 'fr' ? 'Recommander à un collègue' : 'Recommend to a colleague'}
                        </Button>
                    </div>

                    {/* Video player — supports MP4/WebM upload OR YouTube/Vimeo embed */}
                    {lesson.video_url && (() => {
                        const embedSrc = toEmbedUrl(lesson.video_url);
                        return embedSrc ? (
                            <div className="rounded-2xl overflow-hidden bg-black aspect-video" data-testid="lesson-video-embed">
                                <iframe
                                    src={embedSrc}
                                    title="Vidéo de la leçon"
                                    className="w-full h-full"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                />
                            </div>
                        ) : (
                            <div className="rounded-2xl overflow-hidden bg-black aspect-video" data-testid="lesson-video">
                                <video controls className="w-full h-full" src={lesson.video_url} preload="metadata" />
                            </div>
                        );
                    })()}

                    {/* Audio player */}
                    {lesson.audio_url && (
                        <div className="card-soft p-5 flex items-center gap-4" data-testid="lesson-audio">
                            <div className="h-12 w-12 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Headphones size={20} /></div>
                            <audio controls className="flex-1" src={lesson.audio_url} preload="metadata">{lang === 'fr' ? 'Audio non supporté' : 'Audio unsupported'}</audio>
                        </div>
                    )}

                    {/* PDF / Excel attachments */}
                    {(lesson.pdf_url || lesson.excel_url) && (
                        <div className="flex flex-wrap gap-3">
                            {lesson.pdf_url && (
                                <a href={lesson.pdf_url} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border hover:border-primary/40 text-sm font-medium"
                                   data-testid="lesson-pdf-link">
                                    <FileText size={16} className="text-primary" /> {lang === 'fr' ? 'Support PDF' : 'PDF material'} <Download size={12} />
                                </a>
                            )}
                            {lesson.excel_url && (
                                <a href={lesson.excel_url} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border hover:border-primary/40 text-sm font-medium"
                                   data-testid="lesson-excel-link">
                                    <FileText size={16} className="text-emerald-600" /> {lang === 'fr' ? 'Modèle Excel' : 'Excel template'} <Download size={12} />
                                </a>
                            )}
                        </div>
                    )}

                    {/* Body — Markdown rendered + sanitized to prevent XSS */}
                    {bodyHtml && (
                        <>
                            {toc.length >= 3 && (
                                <div className="card-soft p-5" data-testid="lesson-toc">
                                    <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-muted-foreground">
                                        <ListTree size={14} className="text-primary" /> {lang === 'fr' ? 'Sommaire' : 'Contents'}
                                    </div>
                                    <ul className="space-y-1.5">
                                        {toc.map((s, i) => (
                                            <li key={s.id} className={s.level === 3 ? 'pl-5' : ''}>
                                                <button type="button" onClick={() => scrollToSection(s.id)}
                                                    className={`text-left text-sm hover:text-primary transition-colors ${s.level === 2 ? 'font-medium text-ink' : 'text-foreground/70'}`}
                                                    data-testid={`lesson-toc-item-${i}`}>
                                                    {s.text}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <article
                            className="prose prose-stone max-w-none leading-relaxed
                                prose-headings:font-heading prose-headings:text-ink prose-headings:scroll-mt-24
                                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-border
                                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                                prose-p:text-foreground/85 prose-p:leading-[1.85]
                                prose-li:my-1.5 prose-li:leading-relaxed prose-li:marker:text-primary
                                prose-strong:text-ink prose-strong:font-semibold
                                prose-em:text-foreground/75
                                prose-blockquote:border-l-primary prose-blockquote:bg-primary-light/40 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:not-italic
                                prose-a:text-primary
                                prose-ol:pl-5 prose-ul:pl-5"
                            data-testid="lesson-body"
                            dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                        </>
                    )}

                    {/* Quiz */}
                    {quiz.length > 0 && (
                        <div className="card-soft p-6 space-y-5" data-testid="lesson-quiz">
                            <div className="flex items-center justify-between">
                                <h3 className="font-heading text-2xl">{lang === 'fr' ? 'Quiz de validation' : 'Validation quiz'}</h3>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">{lang === 'fr' ? `Seuil ${passScore}%` : `Pass ${passScore}%`}</div>
                            </div>
                            {quiz.map((q, qi) => (
                                <div key={qi} className="space-y-2">
                                    <div className="font-medium">{qi + 1}. {q.q}</div>
                                    <div className="space-y-1.5">
                                        {(q.options || []).map((opt, oi) => {
                                            const isPicked = answers[qi] === oi;
                                            const isRight = quizSubmitted && oi === q.correct;
                                            const isWrong = quizSubmitted && isPicked && oi !== q.correct;
                                            return (
                                                <label key={`${oi}-${opt}`}
                                                       className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${
                                                           isRight ? 'border-emerald-500 bg-emerald-50' :
                                                           isWrong ? 'border-red-500 bg-red-50' :
                                                           isPicked ? 'border-primary bg-primary-light' :
                                                           'border-border hover:bg-surface-muted'
                                                       }`}>
                                                    <input type="radio" name={`q-${qi}`} checked={isPicked} disabled={quizSubmitted}
                                                           onChange={() => setAnswers(a => ({ ...a, [qi]: oi }))}
                                                           data-testid={`lesson-quiz-q${qi}-o${oi}`} />
                                                    <span className="text-sm">{opt}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {quizSubmitted && q.explain && (
                                        <div className="text-xs text-muted-foreground italic mt-1">💡 {q.explain}</div>
                                    )}
                                </div>
                            ))}
                            <div className="flex items-center gap-3 pt-2">
                                {!quizSubmitted ? (
                                    <Button onClick={submitQuiz} disabled={savingProgress} className="bg-primary hover:bg-primary-hover" data-testid="lesson-quiz-submit">
                                        {savingProgress ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                                        {lang === 'fr' ? 'Soumettre le quiz' : 'Submit quiz'}
                                    </Button>
                                ) : (
                                    <>
                                        <div className={`font-heading text-2xl ${quizScore >= passScore ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {quizScore}%
                                        </div>
                                        {quizScore < passScore && (
                                            <Button variant="outline" onClick={() => { setQuizSubmitted(false); setAnswers({}); setQuizScore(null); }}>
                                                {lang === 'fr' ? 'Réessayer' : 'Retry'}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* No quiz -> mark complete button */}
                    {quiz.length === 0 && !completed && (
                        <Button onClick={markCompleteNoQuiz} disabled={savingProgress}
                                className="bg-primary hover:bg-primary-hover" data-testid="lesson-mark-complete">
                            {savingProgress ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
                            {lang === 'fr' ? 'Marquer comme terminée' : 'Mark as complete'}
                        </Button>
                    )}

                    {/* Prev / Next nav */}
                    <div className="flex items-center justify-between gap-4 pt-6 border-t border-border">
                        {prevLesson ? (
                            <Link to={`/app/formation/lecon/${prevLesson.id}`}
                                  className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
                                <ArrowLeft size={14} className="mr-1.5" /> {lang === 'fr' ? prevLesson.title_fr : prevLesson.title_en}
                            </Link>
                        ) : <span />}
                        {nextLesson ? (
                            <Link to={`/app/formation/lecon/${nextLesson.id}`}
                                  className="inline-flex items-center text-sm text-primary hover:underline">
                                {lang === 'fr' ? nextLesson.title_fr : nextLesson.title_en} <ArrowRight size={14} className="ml-1.5" />
                            </Link>
                        ) : <span />}
                    </div>
                </div>

                {/* SIDEBAR — AI tutor */}
                <aside className="lg:col-span-1">
                    {lesson.ai_enabled ? (
                        <div className="card-soft p-5 sticky top-6 flex flex-col h-[calc(100vh-8rem)]" data-testid="lesson-ai-panel">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                    <Sparkles size={16} />
                                </div>
                                <div>
                                    <div className="font-heading text-lg leading-tight">Campus IA</div>
                                    <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
                                        {lang === 'fr' ? 'Tuteur contextuel' : 'Contextual tutor'}
                                    </div>
                                </div>
                            </div>
                            <div ref={aiScrollRef} className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1" data-testid="lesson-ai-history">
                                {aiHistory.length === 0 && (
                                    <div className="text-xs text-muted-foreground italic px-2">
                                        {lang === 'fr'
                                            ? 'Posez une question sur cette leçon. Exemples : "Donne-moi un exemple africain", "Explique-moi ce concept", "Crée un quiz"'
                                            : 'Ask a question. Try: "Give me an African example", "Explain this concept", "Create a quiz"'}
                                    </div>
                                )}
                                {aiHistory.map((m, i) => (
                                    <div key={m.id || i} className={`rounded-xl p-3 text-sm ${m.role === 'user' ? 'bg-primary-light text-primary-foreground text-foreground ml-4' : 'bg-surface-muted mr-4'}`}>
                                        <div className="whitespace-pre-wrap">{m.content}</div>
                                    </div>
                                ))}
                                {aiBusy && (
                                    <div className="bg-surface-muted rounded-xl p-3 text-sm mr-4 inline-flex items-center gap-2 text-muted-foreground">
                                        <Loader2 size={12} className="animate-spin" /> {lang === 'fr' ? 'Campus IA réfléchit…' : 'Thinking…'}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Textarea value={aiInput} onChange={(e) => setAiInput(e.target.value)}
                                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAi(); } }}
                                          rows={2} placeholder={lang === 'fr' ? 'Votre question…' : 'Your question…'}
                                          className="resize-none text-sm" data-testid="lesson-ai-input" />
                                <Button size="icon" onClick={askAi} disabled={aiBusy || !aiInput.trim()}
                                        className="bg-primary hover:bg-primary-hover" data-testid="lesson-ai-send">
                                    <Send size={14} />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="card-soft p-5 text-sm text-muted-foreground">
                            {lang === 'fr' ? 'Assistant IA désactivé pour cette leçon.' : 'AI assistant disabled for this lesson.'}
                        </div>
                    )}
                </aside>
            </div>

            {/* Share dialog */}
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                <DialogContent data-testid="lesson-share-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">{lang === 'fr' ? 'Recommander cette leçon' : 'Recommend this lesson'}</DialogTitle>
                    </DialogHeader>
                    {members === null ? (
                        <div className="py-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={16} /></div>
                    ) : members.length === 0 ? (
                        <p className="text-sm text-foreground/60 py-6 text-center">{lang === 'fr' ? 'Aucun autre membre dans cet espace.' : 'No other members in this workspace.'}</p>
                    ) : (
                        <div className="divide-y divide-border max-h-80 overflow-y-auto">
                            {members.map(m => (
                                <div key={m.user_id} className="flex items-center justify-between py-3 gap-3" data-testid={`share-member-${m.user_id}`}>
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm truncate">{m.profiles?.full_name || m.profiles?.email}</div>
                                        {m.profiles?.full_name && <div className="text-xs text-muted-foreground truncate">{m.profiles?.email}</div>}
                                    </div>
                                    <Button size="sm" onClick={() => recommend(m)} disabled={sharing === m.user_id}
                                        className="bg-primary hover:bg-primary-hover shrink-0" data-testid={`share-send-${m.user_id}`}>
                                        {sharing === m.user_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} className="mr-1.5" />}
                                        {lang === 'fr' ? 'Recommander' : 'Recommend'}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
